import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { schoolService } from '../../../services';
import {
  DEFAULT_ADMIN_LIST_SORT,
  normalizeAdminListSort,
} from '../../../lib/admin-list-sort';
import type { 
  School, 
  SchoolFilters, 
  SchoolCreateRequest, 
  SchoolUpdateRequest,
  SchoolQueryKey 
} from '../types';

// URL state parsers
const typeParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const searchParser = parseAsString.withDefault('');
const sortParser = parseAsString.withDefault(DEFAULT_ADMIN_LIST_SORT);
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

const useSchool = () => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [type, setType] = useQueryState('type', typeParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [search, setSearch] = useQueryState('search', searchParser);
  const [sort, setSort] = useQueryState('sort', sortParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters: SchoolFilters = useMemo(() => ({
    type: type as any || undefined,
    status: status as any || undefined,
    search: search || undefined,
    sort: normalizeAdminListSort(sort),
  }), [type, status, search, sort]);

  // Query key cho caching
  const queryKey: SchoolQueryKey = ['schools', filters, { page, per_page }];

  // Fetch schools với react-query
  const {
    data: schoolsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => schoolService.getSchools({ ...filters, page, per_page }),
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Fetch single school
  const useSchoolDetail = (id: string) => {
    return useQuery({
      queryKey: ['school', id],
      queryFn: () => schoolService.getSchool(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });
  };

  // Create school mutation
  const createSchoolMutation = useMutation({
    mutationFn: (data: SchoolCreateRequest) => schoolService.createSchool(data),
    onSuccess: (newSchool) => {
      // Invalidate và refetch schools list
      queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
  });

  // Update school mutation
  const updateSchoolMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchoolUpdateRequest }) =>
      schoolService.updateSchool(id, data),
    onSuccess: (updatedSchool) => {
      // Update cache cho school detail
      queryClient.setQueryData(['school', updatedSchool.id], updatedSchool);
      // Invalidate schools list
      queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
  });

  // Delete school mutation
  const deleteSchoolMutation = useMutation({
    mutationFn: (id: string) => schoolService.deleteSchool(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
  });

  // Toggle school status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      schoolService.toggleSchoolStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['school', id] });
      queryClient.invalidateQueries({ queryKey: ['schools'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<SchoolFilters>) => {
    if (newFilters.type !== undefined) setType(newFilters.type || null);
    if (newFilters.status !== undefined) setStatus(newFilters.status || null);
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.sort !== undefined) setSort(normalizeAdminListSort(newFilters.sort));
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [setType, setStatus, setSearch, setSort, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setType(null);
    setStatus(null);
    setSearch(null);
    setSort(null);
    setPage(1);
  }, [setType, setStatus, setSearch, setSort, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof SchoolFilters) => {
    switch (filterKey) {
      case 'type':
        setType(null);
        break;
      case 'status':
        setStatus(null);
        break;
      case 'search':
        setSearch(null);
        break;
      case 'sort':
        setSort(null);
        break;
    }
    setPage(1);
  }, [setType, setStatus, setSearch, setSort, setPage]);

  // Backend trả pagination theo format Laravel paginator (current_page/last_page),
  // còn UI đọc page/totalPages — chuẩn hoá ở đây, giống useClasses.
  const pagination = useMemo(() => {
    const raw = schoolsData?.pagination as any;
    if (!raw) return undefined;

    return {
      page: raw.current_page || raw.page || 1,
      per_page: raw.per_page || per_page,
      total: raw.total || 0,
      totalPages: raw.last_page || raw.totalPages || 1,
    };
  }, [schoolsData, per_page]);

  return {
    // Data
    schools: schoolsData?.data || [],
    pagination,

    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createSchool: createSchoolMutation.mutate,
    updateSchool: updateSchoolMutation.mutate,
    deleteSchool: deleteSchoolMutation.mutate,
    toggleStatus: toggleStatusMutation.mutate,

    // Mutation states
    isCreating: createSchoolMutation.isPending,
    isUpdating: updateSchoolMutation.isPending,
    isDeleting: deleteSchoolMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createSchoolMutation,
    updateSchoolMutation,
    deleteSchoolMutation,
    toggleStatusMutation,

    // Single school hook
    useSchoolDetail,
  };
};

export default useSchool;
